import { Skeleton } from "@/components/ui/skeleton";
import InfoTooltip from "@/components/ui/InfoTooltip";
import { useDashboard } from "@/contexts/DashboardContext";
import { LEGEND_WRAPPER_STYLE, CHART_DIMENSIONS, GRID_CONFIG, TICK_CONFIG, getTooltipCursorFill, CHART_MARGINS } from "@/lib/chartConfig";
import { formatTooltipTime, formatXAxisTick, GroupByType } from "@/utils/dateUtils";
import React, { useEffect, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';
import { Info } from "lucide-react";

// Color constants for Inbound calls
const INBOUND_COLORS = {
  light: '#23407A',
  dark: '#1681F6'
};

interface CallVolumeWidgetData {
  time: Date;
  inboundCall: number;
  outboundCall: number;
}

/**
 *
 * @param dateRangeType
 * @param dateRange
 *
 * - When dateRange is the same day, return "hour"
 * - When dateRange is the same month, return "day"
 * - When dateRange is the same year, return "month"
 * - When dateRange is not the same year, return "day"
 *
 * @returns
 *
 */
const getGroupByFromDateRange = (dateRangeType: string, dateRange?: { from: Date, to: Date }): GroupByType => {
  if (!dateRange) return "day";

  const isDateRangeTheSameYear = dateRange.from.getFullYear() === dateRange.to.getFullYear();
  const isDateRangeTheSameMonth = dateRange.from.getMonth() === dateRange.to.getMonth() && isDateRangeTheSameYear;
  const isDateRangeTheSameDay = dateRange.from.getDate() === dateRange.to.getDate() && isDateRangeTheSameMonth && isDateRangeTheSameYear;

  if (isDateRangeTheSameDay) {
    return "hour";
  } else if (isDateRangeTheSameYear) {
    return "day";
  } else {
    return "daydiffyear";
  }
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const CustomTooltip = ({ active, payload, label, dateRange, theme, groupBy }: any) => {
  if (active && payload && payload.length) {
    const currentInboundColor = theme === 'dark' ? INBOUND_COLORS.dark : INBOUND_COLORS.light;

    return (
      <div className="bg-white dark:bg-gray-800 p-3 border dark:border-gray-600 rounded shadow-lg">
        <p className="font-semibold dark:text-white">{formatTooltipTime(label, groupBy)}</p>
        <p style={{ color: currentInboundColor }}>Inbound: {payload[0].value}</p>
      </div>
    );
  }
  return null;
};

const CallVolumeWidget: React.FC<{ data: CallVolumeWidgetData[]; dateRangeType: string, dateRange: { from: Date, to: Date }, icon?: React.ReactNode; loading?: boolean; error?: string }> = ({ data, dateRangeType, dateRange, loading }) => {
  const { theme } = useDashboard();
  const [tickGap, setTickGap] = useState<number>(1);

  // Convert data to local timezone and group by month if needed
  const processedData = data?.map(item => ({
    ...item,
    time: new Date(item.time)
  }));

  // Update dateRange based on data if available
  if (processedData && processedData.length > 0) {
    const times = processedData.map(item => item.time.getTime());
    const minTime = new Date(Math.min(...times));
    const maxTime = new Date(Math.max(...times));
    dateRange = {
      from: minTime,
      to: maxTime
    };
  }

  const groupBy: GroupByType = getGroupByFromDateRange(dateRangeType, dateRange);

  // Check if all data points are 0
  const isAllDataZero = processedData?.every(item =>
    item.inboundCall === 0
  ) ?? false;

  // Calculate tickGap based on container width and data points
  const containerRef = React.useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (containerRef.current && processedData?.length) {
      const items = processedData.length;
      const width = (containerRef.current.clientWidth || containerRef.current.offsetWidth || 600) - 60 - 25;
      const required = groupBy === 'daydiffyear' ? 80 : 60;
      const calculated = Math.ceil(required / (width / items));
      setTickGap(calculated);
    }
  }, [groupBy, processedData]);

  // Get current colors based on theme
  const currentInboundColor = theme === 'dark' ? INBOUND_COLORS.dark : INBOUND_COLORS.light;

  return (
    <div ref={containerRef} className="w-full min-w-[200px] rounded-lg border flex flex-col bg-white dark:bg-[#0C3451] dark:text-white dark:border-[#0C3451] p-3">
      <div className="flex flex-row items-center gap-2">
        <div className="text-[14x] font-[500] text-primary dark:text-white truncate">Call Volume</div>
        <InfoTooltip
          content="The total number of inbound calls within the given time."
          iconClassName="text-primary dark:text-white"
        />
      </div>
      <div>
        {loading ? (
          <Skeleton className="h-56 w-full my-3" />
        ) : processedData?.length === 0 ? (
          <div className="flex items-center justify-center h-[300px] text-sm text-muted-foreground dark:text-white/60">
            <div className="flex flex-col items-center gap-2">
              <span>No call records were found</span>
            </div>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={CHART_DIMENSIONS.height}>
            <BarChart data={processedData} margin={CHART_MARGINS.default}>
              <CartesianGrid strokeDasharray="3 3"/>
              <XAxis
                dataKey="time"
                tick={TICK_CONFIG}
                tickFormatter={(value) => formatXAxisTick(value, groupBy)}
                stroke="currentColor"
                interval="equidistantPreserveStart"
              />
              <YAxis
                tick={TICK_CONFIG}
                stroke="currentColor"
                domain={isAllDataZero ? [0, 10] : undefined}
                ticks={isAllDataZero ? [0, 2, 4, 6, 8, 10] : undefined}
                allowDecimals={false}
              />
              <Tooltip content={(props) => <CustomTooltip {...props} dateRange={dateRangeType} theme={theme} groupBy={groupBy} />} cursor={getTooltipCursorFill(theme)} />
              <Legend
                verticalAlign="top"
                wrapperStyle={LEGEND_WRAPPER_STYLE}
                align="center"
                iconType="rect"
                formatter={(value) => (
                  <span style={{ color: currentInboundColor }}>{value}</span>
                )}
              />
              <Bar dataKey="inboundCall" name="Inbound" fill={currentInboundColor} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
};

export default CallVolumeWidget;
